import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import type { GrRewardRuleResponse } from '@/lib/api';

export interface RuleFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gameConfigId: string;
  /** When set, the dialog is in "edit" mode for this rule. When null, it is in "create" mode. */
  editingRule: GrRewardRuleResponse | null;
  onSaved: () => void;
}

export function RuleFormDialog({
  open,
  onOpenChange,
  gameConfigId,
  editingRule,
  onSaved,
}: RuleFormDialogProps) {
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [gameEvent, setGameEvent] = useState('');
  const [rewardType, setRewardType] = useState('');
  const [m, setM] = useState('1');
  const [b, setB] = useState('0');
  const [maxRewardCount, setMaxRewardCount] = useState('100');
  const [bronzeMultiplier, setBronzeMultiplier] = useState('2');
  const [silverMultiplier, setSilverMultiplier] = useState('3');
  const [goldMultiplier, setGoldMultiplier] = useState('6');
  const [baseMultiplier, setBaseMultiplier] = useState('1');
  const [useSteps, setUseSteps] = useState(false);
  const [stepsJson, setStepsJson] = useState('');

  // Reset or populate form when the dialog opens or the editing target changes
  useEffect(() => {
    if (!open) return;

    if (editingRule) {
      setGameEvent(editingRule.gameEvent);
      setRewardType(editingRule.rewardType);
      setM(String(editingRule.m));
      setB(String(editingRule.b));
      setMaxRewardCount(String(editingRule.maxRewardCount));
      setBronzeMultiplier(String(editingRule.bronzeMultiplier));
      setSilverMultiplier(String(editingRule.silverMultiplier));
      setGoldMultiplier(String(editingRule.goldMultiplier));
      setBaseMultiplier(String(editingRule.baseMultiplier));
      setUseSteps(!!editingRule.steps);
      setStepsJson(editingRule.steps ? JSON.stringify(editingRule.steps, null, 2) : '');
    } else {
      setGameEvent('');
      setRewardType('');
      setM('1');
      setB('0');
      setMaxRewardCount('100');
      setBronzeMultiplier('2');
      setSilverMultiplier('3');
      setGoldMultiplier('6');
      setBaseMultiplier('1');
      setUseSteps(false);
      setStepsJson('');
    }
    setFormError(null);
  }, [open, editingRule]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!gameConfigId) return;

    const trimmedRewardType = rewardType.trim();
    if (!trimmedRewardType) return;

    setSaving(true);
    setFormError(null);
    try {
      // Search DFE definitions by name to resolve the itemDefId
      const searchResult = await api.listDfeDefinitions({ search: trimmedRewardType, limit: 50 });
      const matchedDef = searchResult.definitions.find(
        (d) => d.name.toLowerCase() === trimmedRewardType.toLowerCase(),
      );
      if (!matchedDef) {
        setFormError(
          `Unknown reward type "${trimmedRewardType}". It must match a DFE inventory item name.`,
        );
        setSaving(false);
        return;
      }

      let parsedSteps = null;
      if (useSteps && stepsJson.trim()) {
        try {
          parsedSteps = JSON.parse(stepsJson);
          if (!Array.isArray(parsedSteps)) {
            setFormError('Steps must be a JSON array');
            setSaving(false);
            return;
          }
        } catch {
          setFormError('Invalid JSON for steps');
          setSaving(false);
          return;
        }
      }

      if (editingRule) {
        await api.updateGameRewardsRule(editingRule.ruleId, {
          m: Number(m),
          b: Number(b),
          maxRewardCount: Number(maxRewardCount),
          bronzeMultiplier: Number(bronzeMultiplier),
          silverMultiplier: Number(silverMultiplier),
          goldMultiplier: Number(goldMultiplier),
          baseMultiplier: Number(baseMultiplier),
          steps: useSteps ? parsedSteps : null,
          itemDefId: matchedDef.itemDefId,
        });
      } else {
        await api.createGameRewardsRule({
          gameConfigId,
          gameEvent: gameEvent.trim(),
          rewardType: trimmedRewardType,
          itemDefId: matchedDef.itemDefId,
          m: Number(m),
          b: Number(b),
          maxRewardCount: Number(maxRewardCount),
          bronzeMultiplier: Number(bronzeMultiplier),
          silverMultiplier: Number(silverMultiplier),
          goldMultiplier: Number(goldMultiplier),
          baseMultiplier: Number(baseMultiplier),
          steps: useSteps ? parsedSteps : null,
        });
      }
      onOpenChange(false);
      onSaved();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{editingRule ? 'Edit Rule' : 'Add Reward Rule'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSave} className="space-y-4">
          {!editingRule && (
            <>
              <div className="space-y-2">
                <Label htmlFor="ruleGameEvent">Game Event *</Label>
                <Input
                  id="ruleGameEvent"
                  value={gameEvent}
                  onChange={(e) => setGameEvent(e.target.value)}
                  placeholder="level_complete"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ruleRewardType">Reward Type *</Label>
                <Input
                  id="ruleRewardType"
                  value={rewardType}
                  onChange={(e) => setRewardType(e.target.value)}
                  placeholder="currency"
                  required
                />
              </div>
            </>
          )}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label htmlFor="ruleM">m (slope)</Label>
              <Input
                id="ruleM"
                type="number"
                step="any"
                value={m}
                onChange={(e) => setM(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ruleB">b (intercept)</Label>
              <Input
                id="ruleB"
                type="number"
                step="any"
                value={b}
                onChange={(e) => setB(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ruleMax">Max Count</Label>
              <Input
                id="ruleMax"
                type="number"
                value={maxRewardCount}
                onChange={(e) => setMaxRewardCount(e.target.value)}
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            {useSteps
              ? 'Step-table: score matched against thresholds'
              : 'Formula: reward = m * score + b (capped at max count)'}
          </p>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="ruleUseSteps"
              checked={useSteps}
              onChange={(e) => setUseSteps(e.target.checked)}
              className="rounded border-input"
            />
            <Label htmlFor="ruleUseSteps" className="text-sm font-normal">
              Use step-table instead of linear formula
            </Label>
          </div>
          {useSteps && (
            <div className="space-y-2">
              <Label htmlFor="ruleSteps">Steps JSON</Label>
              <textarea
                id="ruleSteps"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono min-h-[80px]"
                value={stepsJson}
                onChange={(e) => setStepsJson(e.target.value)}
                placeholder={'[\n  {"minScore": 0, "maxScore": 999999, "reward": 10}\n]'}
              />
              <p className="text-xs text-muted-foreground">
                Array of {'{'}minScore, maxScore, reward{'}'} objects
              </p>
            </div>
          )}
          <div className="grid grid-cols-4 gap-3">
            <div className="space-y-2">
              <Label htmlFor="ruleBase">Base x</Label>
              <Input
                id="ruleBase"
                type="number"
                step="any"
                value={baseMultiplier}
                onChange={(e) => setBaseMultiplier(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ruleBronze">Bronze x</Label>
              <Input
                id="ruleBronze"
                type="number"
                step="any"
                value={bronzeMultiplier}
                onChange={(e) => setBronzeMultiplier(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ruleSilver">Silver x</Label>
              <Input
                id="ruleSilver"
                type="number"
                step="any"
                value={silverMultiplier}
                onChange={(e) => setSilverMultiplier(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ruleGold">Gold x</Label>
              <Input
                id="ruleGold"
                type="number"
                step="any"
                value={goldMultiplier}
                onChange={(e) => setGoldMultiplier(e.target.value)}
              />
            </div>
          </div>

          {formError && <p className="text-sm text-destructive">{formError}</p>}

          <Button type="submit" className="w-full" disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {editingRule ? 'Update Rule' : 'Create Rule'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
